import type { AutomationItem, AutomationRunItem, AutomationRule } from '@hygge/shared';
import { api } from './api';

// ─── Types ──────────────────────────────────────────────────────────────

export interface UpsertAutomation extends AutomationRule {
  name: string;
  description?: string;
  enabled?: boolean;
}

export interface AutomationRecipe extends AutomationRule {
  key: string;
  name: string;
  description: string;
}

export interface TestRunResult {
  matched: boolean;
  wouldRun: { type: string }[];
}

// ─── Automations ────────────────────────────────────────────────────────

export async function getAutomations(boardId: string): Promise<AutomationItem[]> {
  const { data } = await api.get<AutomationItem[]>(`/boards/${boardId}/automations`);
  return data;
}

export async function createAutomation(
  boardId: string,
  body: UpsertAutomation
): Promise<AutomationItem> {
  const { data } = await api.post<AutomationItem>(`/boards/${boardId}/automations`, body);
  return data;
}

export async function updateAutomation(
  id: string,
  body: Partial<UpsertAutomation>
): Promise<AutomationItem> {
  const { data } = await api.patch<AutomationItem>(`/automations/${id}`, body);
  return data;
}

export async function deleteAutomation(id: string): Promise<void> {
  await api.delete(`/automations/${id}`);
}

// ─── Runs ────────────────────────────────────────────────────────────────

export async function getAutomationRuns(id: string): Promise<AutomationRunItem[]> {
  const { data } = await api.get<AutomationRunItem[]>(`/automations/${id}/runs`);
  return data;
}

// ─── Test ────────────────────────────────────────────────────────────────

export async function testAutomation(id: string, taskKey: string): Promise<TestRunResult> {
  const { data } = await api.post<TestRunResult>(`/automations/${id}/test`, { taskKey });
  return data;
}

// ─── Recipes ────────────────────────────────────────────────────────────

export async function getAutomationRecipes(): Promise<AutomationRecipe[]> {
  const { data } = await api.get<AutomationRecipe[]>(`/automations/recipes`);
  return data;
}

// ─── AI Generate ────────────────────────────────────────────────────────

export interface GenerateAutomationResult {
  rule: AutomationRule;
  warnings: string[];
}

export async function generateAutomation(
  boardId: string,
  body: { prompt: string; currentRule?: AutomationRule }
): Promise<GenerateAutomationResult> {
  const { data } = await api.post<GenerateAutomationResult>(
    `/boards/${boardId}/automations/generate`,
    body
  );
  return data;
}
